import React, { ReactNode, useRef } from 'react';
import { styled, useMediaQuery, useTheme } from '@mui/material';

import CustomSlickSlider from '@/components/CustomSlickSlider';
import { PrevArrowButton, NextArrowButton } from './NavigationButtons';

interface ContentCarouselProps {
  lengthItem: number;
  arrowColor?: string;
  children: ReactNode;
  defaultSlidesToShow?: number;
  defaultSlidesToShowMobile?: number;
  className?: string;
  containerStyle?: any;
  infinite?: boolean;
  hideArrowsIfCantScroll?: boolean;
}

const SliderWrap = styled('div')`
  margin-bottom: 3.75rem;
  .slick-slider {
    position: relative !important;
  }
  .slick-track {
    display: flex !important;
    margin: 0 !important;
    .slick-slide {
      margin: 0 8px !important;
    }
  }
  .slick-next:before,
  .slick-prev:before {
    content: '' !important;
  }

  .slick-prev:hover,
  .slick-prev:focus,
  .slick-next:hover,
  .slick-next:focus {
    color: ${({ theme }) => theme.palette.grey[200]};
    outline: inherit;
    background: ${({ theme }) => theme.palette.grey[50]};
  }
`;

const ContentCarousel = ({
  children,
  arrowColor,
  lengthItem,
  defaultSlidesToShow,
  defaultSlidesToShowMobile,
  className,
  containerStyle,
  infinite,
  hideArrowsIfCantScroll,
}: ContentCarouselProps): JSX.Element => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(`${theme.breakpoints.up('lg')}`);
  const sliderRef = useRef(null);

  const gotoNext = () => {
    sliderRef.current?.slickNext();
  };

  const gotoPrev = () => {
    sliderRef.current?.slickPrev();
  };

  const settings = {
    infinite: infinite ?? lengthItem > 4,
    speed: 500,
    slidesToShow: defaultSlidesToShow || 4,
    slidesToScroll: 1,
    nextArrow: (
      <NextArrowButton
        arrowColor={arrowColor}
        onClick={gotoNext}
        hideArrowsIfCantScroll={hideArrowsIfCantScroll}
        defaultSlidesToShow={
          isDesktop ? defaultSlidesToShow : defaultSlidesToShowMobile
        }
      />
    ),
    prevArrow: (
      <PrevArrowButton
        arrowColor={arrowColor}
        onClick={gotoPrev}
        hideArrowsIfCantScroll={hideArrowsIfCantScroll}
      />
    ),
    responsive: [
      {
        breakpoint: 1279,
        settings: {
          slidesToScroll: 1,
          infinite: infinite ?? lengthItem > 3,
          slidesToShow: defaultSlidesToShow || 3,
        },
      },
      {
        breakpoint: 1023,
        settings: {
          infinite: infinite ?? lengthItem > 2,
          slidesToShow: defaultSlidesToShowMobile || 2,
          slidesToScroll: 1,
        },
      },
    ],
  };

  return (
    <SliderWrap style={containerStyle}>
      <CustomSlickSlider ref={sliderRef} {...settings} className={className}>
        {children}
      </CustomSlickSlider>
    </SliderWrap>
  );
};

ContentCarousel.defaultProps = {
  arrowColor: null,
  defaultSlidesToShow: null,
  defaultSlidesToShowMobile: null,
  className: null,
};

export default ContentCarousel;
